<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { buildCustomSlaHistory } from 'dashboard/helper/customSlaStatus';
import wootConstants from 'dashboard/constants/globals';
import CustomSlaHistoryItem from './CustomSlaHistoryItem.vue';
import CustomSlaNextResponseCounts from './CustomSlaNextResponseCounts.vue';

const props = defineProps({
  customSlaMissedEvents: {
    type: Array,
    required: true,
  },
  appliedSla: {
    type: Object,
    default: null,
  },
});

const { CUSTOM_SLA_MISS_TYPES } = wootConstants;

const { t } = useI18n();

// One Met/Breached-with-duration line per once-only threshold type (first response,
// resolution) that has already been fulfilled — this is the "which were met and in how
// much time, which weren't and in how much time" history the badge summarizes on hover.
const history = computed(() =>
  buildCustomSlaHistory({
    appliedSla: props.appliedSla,
    events: props.customSlaMissedEvents,
  })
);

const historyLabel = type =>
  ({
    [CUSTOM_SLA_MISS_TYPES.FIRST_RESPONSE]: t(
      'CUSTOM_SLA.EVENTS.FIRST_RESPONSE'
    ),
    [CUSTOM_SLA_MISS_TYPES.RESOLUTION]: t('CUSTOM_SLA.EVENTS.RESOLUTION'),
  })[type];

// Next response is a repeating threshold with no single "met" outcome, so it gets a
// met/missed count pair instead of a Met/Breached-with-duration history entry — shown
// only when the policy actually configures a next-response rule, and always as two lines
// (defaulting to 0) rather than being hidden when a count is zero.
const showNextResponseCounts = computed(
  () => props.appliedSla?.sla_next_response_time_threshold != null
);
const nextResponseMetCount = computed(
  () => props.appliedSla?.next_response_met_count || 0
);
const nextResponseMissedCount = computed(
  () => props.appliedSla?.next_response_missed_count || 0
);
</script>

<template>
  <div
    class="absolute flex flex-col items-start border-n-strong bg-n-solid-3 w-96 backdrop-blur-[100px] px-6 py-5 z-50 shadow rounded-xl gap-4 max-h-96 overflow-auto"
  >
    <span class="text-sm font-medium text-n-slate-12">
      {{ $t('CUSTOM_SLA.EVENTS.TITLE') }}
    </span>
    <CustomSlaHistoryItem
      v-for="entry in history"
      :key="entry.type"
      :label="historyLabel(entry.type)"
      :is-breached="entry.isBreached"
      :duration-seconds="entry.durationSeconds"
    />
    <CustomSlaNextResponseCounts
      v-if="showNextResponseCounts"
      :met-count="nextResponseMetCount"
      :missed-count="nextResponseMissedCount"
    />
  </div>
</template>
